import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileTextIcon, SettingsIcon, ActivityIcon, CheckIcon } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function PMPMOPage() {
  return <div className="w-full" data-id="element-2131" data-mp="7jancen570hj">
      {/* HERO SECTION */}
      <section className="section-navy hero-grid-pattern py-12" data-id="element-2132" data-mp="2mk5blrs1uth">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2133" data-mp="3tslbph0ss08s">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} data-id="element-2134" data-mp="3tuob2rnub5q6">
            <div className="text-sm font-bold text-nrs-light mb-4 uppercase tracking-wider" data-id="element-2135" data-mp="3oua47cxz61tq">
              <Link to="/services" className="hover:text-nrs-orange" data-id="element-2136" data-mp="3kt8atcqqojzo">
                Services
              </Link>{' '}
              &gt;{' '}
              <Link to="/services/program-and-project-management" className="hover:text-nrs-orange" data-id="element-2137" data-mp="1mzsvhrmgf07j">
                Program Management
              </Link>{' '}
              &gt; PMO
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight" data-id="element-2138" data-mp="mgrruy2flrjv">
              PMO Establishment & Operations
            </h1>
            <div className="h-1.5 w-24 bg-nrs-orange rounded-full mb-6" data-id="element-2139" data-mp="8i1tzg7sfnug"></div>
          </motion.div>
        </div>
      </section>

      {/* PMO OVERVIEW */}
      <section className="section-white border-b border-gray-200" data-id="element-2140" data-mp="1086cf9bzm0t1">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2141" data-mp="108y8smdes2kd">
          <h2 className="text-3xl font-bold mb-8" data-id="element-2142" data-mp="1567vvvdbs251">
            PMO Establishment and Operations
          </h2>
          <div className="max-w-4xl space-y-6 text-gray-600 leading-relaxed text-lg" data-id="element-2143" data-mp="rf339d8dka47">
            <p data-id="element-2144" data-mp="vgekovh3hp1b">
              A high-functioning Program Management Office (PMO) is the engine
              that drives strategic execution. For federal agencies and
              enterprises managing complex IT portfolios, a PMO provides the
              visibility, governance, and standardization required to ensure
              investments deliver mission value.
            </p>
            <p data-id="element-2145" data-mp="1trw2vcglw4wu">
              New River Systems helps agencies stand up new PMOs from scratch or
              optimize existing ones. We understand that one size does not fit
              all—we design supportive, controlling, or directive PMOs based on
              your agency's culture and maturity level.
            </p>
            <p data-id="element-2146" data-mp="1ewg4iv8ztmun">
              Our approach combines rigorous governance frameworks with modern
              portfolio management tools (like ServiceNow SPM or Jira Align) to
              create PMOs that enable delivery rather than creating bureaucratic
              bottlenecks.
            </p>
          </div>
        </div>
      </section>

      {/* PMO SERVICES */}
      <section className="section-light-blue" data-id="element-2147" data-mp="w4nbj0txc1mx">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2148" data-mp="w6hxmigxlzsl">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-2149" data-mp="r6lrackrhw9v">
            PMO Service Tiers
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-2150" data-mp="h0uadr581lin"></div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2151" data-mp="lzf91m4trx4z">
            {/* Tier 1 */}
            <div className="card flex flex-col border-t-4 border-t-nrs-navy" data-id="element-2152" data-mp="r0l61w5iq6kb">
              <FileTextIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-2153" data-mp="vz68814bz0be" />
              <h3 className="text-xl font-bold mb-4" data-id="element-2154" data-mp="vwa949gqy5ld">
                Tier 1: PMO Charter & Framework Design
              </h3>
              <ul className="space-y-2 text-gray-600 mb-6 flex-grow" data-id="element-2155" data-mp="39fs5mj51ocn">
                <li className="flex items-start gap-2" data-id="element-2156" data-mp="37mlw8cft656">
                  <span className="text-nrs-orange mt-1" data-id="element-2157" data-mp="88aa6m9a78vp">•</span> PMO charter
                  development
                </li>
                <li className="flex items-start gap-2" data-id="element-2158" data-mp="d1vawm29egyl">
                  <span className="text-nrs-orange mt-1" data-id="element-2159" data-mp="3boppyifzw7em">•</span> Governance
                  framework (decision rights, escalation paths)
                </li>
                <li className="flex items-start gap-2" data-id="element-2160" data-mp="1m8n3z0sbhaty">
                  <span className="text-nrs-orange mt-1" data-id="element-2161" data-mp="1m96mailvq18i">•</span> RACI matrix
                  for program roles
                </li>
                <li className="flex items-start gap-2" data-id="element-2162" data-mp="1h7po4uus4svm">
                  <span className="text-nrs-orange mt-1" data-id="element-2163" data-mp="1vz8cph1pfvql">•</span> Process
                  documentation (templates, playbooks, checklists)
                </li>
                <li className="flex items-start gap-2" data-id="element-2164" data-mp="24yv3l8wt5j5e">
                  <span className="text-nrs-orange mt-1" data-id="element-2165" data-mp="24xblrdvi701i">•</span> Metrics and
                  KPI definition
                </li>
                <li className="flex items-start gap-2" data-id="element-2166" data-mp="2exat57g6skcs">
                  <span className="text-nrs-orange mt-1" data-id="element-2167" data-mp="2ewsopnjdezts">•</span> PMO org chart
                  and staffing model
                </li>
              </ul>
              <div className="bg-nrs-light p-4 rounded-md text-sm" data-id="element-2168" data-mp="kj1gmsgowo0g">
                <p data-id="element-2169" data-mp="kle0jb60d8g5">
                  <span className="font-bold text-nrs-navy" data-id="element-2170" data-mp="fk6t5d7s2ba0">Duration:</span>{' '}
                  30-60 days
                </p>
                <p className="mt-1" data-id="element-2171" data-mp="2r7absp08p61t">
                  <span className="font-bold text-nrs-navy" data-id="element-2172" data-mp="2r8bu9ejl4utk">Deliverables:</span>{' '}
                  PMO Charter, Governance Framework, Process Documentation
                </p>
              </div>
            </div>

            {/* Tier 2 */}
            <div className="card flex flex-col border-t-4 border-t-nrs-orange" data-id="element-2173" data-mp="2mgbq19whffr6">
              <SettingsIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-2174" data-mp="28h29ua8a0ln7" />
              <h3 className="text-xl font-bold mb-4" data-id="element-2175" data-mp="28fr78dqkhjxo">
                Tier 2: PMO Tool Implementation
              </h3>
              <ul className="space-y-2 text-gray-600 mb-6 flex-grow" data-id="element-2176" data-mp="2ddssn5bgtvcb">
                <li className="flex items-start gap-2" data-id="element-2177" data-mp="1ykphpgsdqfuk">
                  <span className="text-nrs-orange mt-1" data-id="element-2178" data-mp="23hfzq9nqvlim">•</span> Tool selection
                  (Jira, MS Project, ServiceNow SPM, Smartsheet)
                </li>
                <li className="flex items-start gap-2" data-id="element-2179" data-mp="3sv2ybynxq2v8">
                  <span className="text-nrs-orange mt-1" data-id="element-2180" data-mp="1pbaj5xmr2bl">•</span> Tool
                  configuration and customization
                </li>
                <li className="flex items-start gap-2" data-id="element-2181" data-mp="72v8senxr0c">
                  <span className="text-nrs-orange mt-1" data-id="element-2182" data-mp="3reywatorb2wx">•</span> Workflow
                  automation
                </li>
                <li className="flex items-start gap-2" data-id="element-2183" data-mp="3mgxcg6jiazke">
                  <span className="text-nrs-orange mt-1" data-id="element-2184" data-mp="3iftzbrsufhmv">•</span> Reporting
                  dashboard creation
                </li>
                <li className="flex items-start gap-2" data-id="element-2185" data-mp="izf8492y0aoq">
                  <span className="text-nrs-orange mt-1" data-id="element-2186" data-mp="e0c1ik7bdwen">•</span> Integration
                  with existing systems (Confluence, SharePoint)
                </li>
                <li className="flex items-start gap-2" data-id="element-2187" data-mp="98135ls82io7">
                  <span className="text-nrs-orange mt-1" data-id="element-2188" data-mp="57h60i7nff22">•</span> User training
                  and adoption
                </li>
              </ul>
              <div className="bg-nrs-light p-4 rounded-md text-sm" data-id="element-2189" data-mp="11s9dcbarkofr">
                <p data-id="element-2190" data-mp="wy557opriad3">
                  <span className="font-bold text-nrs-navy" data-id="element-2191" data-mp="wxllc2gjxbwf">Duration:</span>{' '}
                  60-120 days
                </p>
                <p className="mt-1" data-id="element-2192" data-mp="2kl9iyzxh2gwa">
                  <span className="font-bold text-nrs-navy" data-id="element-2193" data-mp="2kmc6qyzi037z">Deliverables:</span>{' '}
                  Configured tools, dashboards, training materials
                </p>
              </div>
            </div>

            {/* Tier 3 */}
            <div className="card flex flex-col border-t-4 border-t-nrs-navy" data-id="element-2194" data-mp="2tjuvoeg9j1p5">
              <ActivityIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-2195" data-mp="2yl232cehtyva" />
              <h3 className="text-xl font-bold mb-4" data-id="element-2196" data-mp="33jdegrkfshfm">
                Tier 3: PMO Operations (Managed Service)
              </h3>
              <ul className="space-y-2 text-gray-600 mb-6 flex-grow" data-id="element-2197" data-mp="38bwz28hjkctx">
                <li className="flex items-start gap-2" data-id="element-2198" data-mp="3cbfa8cjk99jk">
                  <span className="text-nrs-orange mt-1" data-id="element-2199" data-mp="3hbth3r9fedg0">•</span> Day-to-day PMO
                  operations
                </li>
                <li className="flex items-start gap-2" data-id="element-2200" data-mp="3ma3d242z5i2o">
                  <span className="text-nrs-orange mt-1" data-id="element-2201" data-mp="3m8jwsdhbj70w">•</span> Portfolio
                  intake and prioritization
                </li>
                <li className="flex items-start gap-2" data-id="element-2202" data-mp="2s9fs3imtt54p">
                  <span className="text-nrs-orange mt-1" data-id="element-2203" data-mp="2sb0m9758am21">•</span> Resource
                  capacity planning and allocation
                </li>
                <li className="flex items-start gap-2" data-id="element-2204" data-mp="327vl5r5eyogu">
                  <span className="text-nrs-orange mt-1" data-id="element-2205" data-mp="324zjpsucxhnn">•</span> Program health
                  monitoring (schedule, budget, risks)
                </li>
                <li className="flex items-start gap-2" data-id="element-2206" data-mp="29jg6j5q72861">
                  <span className="text-nrs-orange mt-1" data-id="element-2207" data-mp="2n9bb7ri0a8dy">•</span> Monthly
                  executive reporting
                </li>
                <li className="flex items-start gap-2" data-id="element-2208" data-mp="2ihs63x91ggam">
                  <span className="text-nrs-orange mt-1" data-id="element-2209" data-mp="1ut905odgoflp">•</span> Continuous
                  process improvement
                </li>
              </ul>
              <div className="bg-nrs-light p-4 rounded-md text-sm" data-id="element-2210" data-mp="1qqvvl3aganqi">
                <p data-id="element-2211" data-mp="1gn6yul9dtbw3">
                  <span className="font-bold text-nrs-navy" data-id="element-2212" data-mp="1gmp5bwe3tbrv">Duration:</span>{' '}
                  Ongoing (annual contracts)
                </p>
                <p className="mt-1" data-id="element-2213" data-mp="1lg9xh7t0nbj5">
                  <span className="font-bold text-nrs-navy" data-id="element-2214" data-mp="16qc3gb8enrm1">Team Size:</span>{' '}
                  1-3 FTE depending on portfolio size
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PMO MATURITY MODEL */}
      <section className="section-white" data-id="element-2215" data-mp="24db2utqhfdnm">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-2216" data-mp="24c9skrhaiv6y">
          <h2 className="text-3xl font-bold text-center mb-4" data-id="element-2217" data-mp="1pgaijbamekz6">
            PMO Maturity Model
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-2218" data-mp="1uelsdm0x0vhe"></div>
          <p className="text-center text-lg text-gray-600 mb-16 max-w-3xl mx-auto" data-id="element-2219" data-mp="e155pegrz61u">
            We assess your current PMO maturity and create a roadmap to elevate
            capabilities.
          </p>

          <div className="space-y-4 max-w-4xl mx-auto relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-nrs-navy before:to-transparent" data-id="element-2220" data-mp="1f94f5r26x21c">
            {/* Level 1 */}
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2221" data-mp="1ahct9ttzbkhj">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-gray-300 text-white font-bold shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-2222" data-mp="wh1wyb9whdoz">
                1
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded border border-gray-200 shadow-sm" data-id="element-2223" data-mp="rkbi1r9u0o55">
                <h4 className="font-bold text-gray-500 mb-1" data-id="element-2224" data-mp="2gyzxy2hmso7x">
                  Level 1: Ad Hoc
                </h4>
                <ul className="text-sm text-gray-600 list-disc ml-4" data-id="element-2225" data-mp="2luxarytorcfy">
                  <li data-id="element-2226" data-mp="aahgotg5qj65">No standardized processes</li>
                  <li data-id="element-2227" data-mp="5bd5xydl4ddp">Inconsistent tools and reporting</li>
                  <li data-id="element-2228" data-mp="58s0kze2vfb7">Reactive project management</li>
                </ul>
              </div>
            </div>

            {/* Level 2 */}
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2229" data-mp="f8iw94edk0cv">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-nrs-light text-nrs-navy font-bold shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-2230" data-mp="sz4fiu7qqcba">
                2
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded border border-gray-200 shadow-sm" data-id="element-2231" data-mp="xz8yqs019l57">
                <h4 className="font-bold text-nrs-navy mb-1" data-id="element-2232" data-mp="34qi63bv1vcep">
                  Level 2: Foundational
                </h4>
                <ul className="text-sm text-gray-600 list-disc ml-4" data-id="element-2233" data-mp="2zszsv9a02b0w">
                  <li data-id="element-2234" data-mp="13pbmmlsjdmh2">Basic PMO charter and governance</li>
                  <li data-id="element-2235" data-mp="18mbt2xtasr8p">Common templates and tools</li>
                  <li data-id="element-2236" data-mp="18n3oa7izgmyg">Monthly status reporting</li>
                </ul>
              </div>
            </div>

            {/* Level 3 */}
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2237" data-mp="1hn9ztnv4fcuz">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-nrs-blue text-white font-bold shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-2238" data-mp="1mljwy40eonj8">
                3
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded border border-nrs-blue/30 shadow-sm" data-id="element-2239" data-mp="20drmbapd1c7w">
                <h4 className="font-bold text-nrs-blue mb-1" data-id="element-2240" data-mp="25efdbwdl9t24">
                  Level 3: Standardized
                </h4>
                <ul className="text-sm text-gray-600 list-disc ml-4" data-id="element-2241" data-mp="2acgwehb0lsdl">
                  <li data-id="element-2242" data-mp="2kh5rvac04swa">Consistent processes across all programs</li>
                  <li data-id="element-2243" data-mp="2fhax2vp3vj5y">Integrated tool suite (Jira, MS Project, etc.)</li>
                  <li data-id="element-2244" data-mp="2fjdy0arsqwpg">Portfolio-level visibility</li>
                  <li data-id="element-2245" data-mp="2aizmgiqzl4mo">Resource capacity planning</li>
                </ul>
              </div>
            </div>

            {/* Level 4 */}
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2246" data-mp="26iftlrdg4n6c">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-nrs-navy text-white font-bold shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-2247" data-mp="2y7sq71duogru">
                4
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded border border-nrs-navy/50 shadow-sm" data-id="element-2248" data-mp="2pdeawytbl68q">
                <h4 className="font-bold text-nrs-navy mb-1" data-id="element-2249" data-mp="3h5v68qof73di">
                  Level 4: Optimized
                </h4>
                <ul className="text-sm text-gray-600 list-disc ml-4" data-id="element-2250" data-mp="31nc0627fixar">
                  <li data-id="element-2251" data-mp="36n6qa3jdriur">Automated workflows and dashboards</li>
                  <li data-id="element-2252" data-mp="36ldk0pcoj0na">Continuous improvement culture</li>
                  <li data-id="element-2253" data-mp="2sogseloqlf5c">Predictive analytics and forecasting</li>
                  <li data-id="element-2254" data-mp="2wpak7q5ave3i">Strategic portfolio alignment</li>
                </ul>
              </div>
            </div>

            {/* Level 5 */}
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2255" data-mp="3kfwhqnhohfz9">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-nrs-orange text-white font-bold shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-2256" data-mp="3pd6gwuuvd60a">
                5
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded border-2 border-nrs-orange shadow-md" data-id="element-2257" data-mp="77jn9akqz7cv">
                <h4 className="font-bold text-nrs-orange mb-1" data-id="element-2258" data-mp="c0w9je1yf22g">
                  Level 5: Leading Practice
                </h4>
                <ul className="text-sm text-gray-600 list-disc ml-4" data-id="element-2259" data-mp="1rqx6a0ljma6m">
                  <li data-id="element-2260" data-mp="1rojcdv2ln40n">Center of excellence for program delivery</li>
                  <li data-id="element-2261" data-mp="26eqlhk6pvaoo">AI/ML-powered insights</li>
                  <li data-id="element-2262" data-mp="21ggpj7d64620">Industry benchmarking</li>
                  <li data-id="element-2263" data-mp="21gp3l2l11hes">Organizational change agent</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-navy" data-id="element-2264" data-mp="18sb4skm46h26">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-2265" data-mp="18tcfgnz7x1jd">
          <h3 className="text-3xl font-bold mb-8 text-white" data-id="element-2266" data-mp="1nqdap1gmbeml">
            Transform Your PMO into a Strategic Asset
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2267" data-mp="1is1yik2uos17">
            <Link to="/contact-us" className="btn-primary" data-id="element-2268" data-mp="2z24b0x8353yr">
              Request a PMO Maturity Assessment
            </Link>
            <Link to="/contact-us" className="btn-secondary border-white text-white hover:bg-white/10 hover:text-white" data-id="element-2269" data-mp="2u77724pnxksq">
              Discuss PMO Services
            </Link>
          </div>
        </div>
      </section>
    </div>;
}